// Path: libs/core/shell/src/lib/layout/public/hero/hero-header/hero-header.component.ts
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { APP_CONFIG, AppConfig } from '@cardapio-online/config';
import { DrawerService } from '@cardapio-online/drawer';
import { ThemeApplierService } from '../../../../theme/theme-applier.service';
import { CartService } from '../../../../cart/cart.service';
import { PublicHeroPromoItem } from '../public-hero-state.service';
import {
  AUTH_CLIENT_INITIAL_STATE,
  AuthClientComponent,
  AuthClientMascotChange,
  AuthClientState,
  AuthenticatedClient,
  ClientAuthService,
  ClientAuthSession,
  ClientsService,
} from '@cardapio-online/clients';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  tap,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalGlobalButton } from '@cardapio-online/modal';
import { MascotState } from '@cardapio-online/mascot';

type AuthMode = 'lookup' | 'login' | 'register' | 'identified';
@Component({
  selector: 'lib-hero-header',
  templateUrl: './hero-header.component.html',
  styleUrl: './hero-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroHeaderComponent {
  readonly promos = input<PublicHeroPromoItem[]>([]);
  readonly bannerImageUrl = input<string | null>(null);

  readonly headline = input<string | null>('Promoção relâmpago só hoje');
  readonly fallbackTitle = input<string | null>(null);
  readonly fallbackSubtitle = input<string | null>(null);

  readonly brandImageUrl = input<string | null>(null);
  readonly searchPlaceholder = input<string>('Buscar produtos');
  readonly isOpen = input<boolean>(true);

  readonly searchSubmitted = output<string>();
  readonly promoSelected = output<PublicHeroPromoItem>();

  protected searchTerm = '';
  private readonly searchInput$ = new Subject<string>();
  private readonly destroyRef = inject(DestroyRef);
  protected readonly cartCount = this.cart.count;

  protected readonly authModalOpen = signal(false);
  protected readonly authClientState = signal<AuthClientState>(
    AUTH_CLIENT_INITIAL_STATE
  );

  private readonly defaultAuthMascotState: MascotState = {
  imageSrc: 'assets/public/sino-sentado.png',
  imageAlt: 'Mascote MenuHub sentado',
  eyes: 'closed',
  size: 'md',
  animation: 'idle',
  thoughtEmoji: '😊',
  thoughtText: 'Bem vindo!',
  thoughtVisible: true,
};


protected readonly authMascotState = signal<MascotState>(
  this.defaultAuthMascotState
);

  private readonly authClient = viewChild(AuthClientComponent);

  protected readonly authModalButtons = computed<ModalGlobalButton[]>(() => {
    const state = this.authClientState();

    return [
      {
        id: 'submit-auth',
        label: state.submitLabel,
        tone: 'primary',
        disabled: state.submitting || state.lookingUp,
      },
    ];
  });

  protected readonly activeSlide = signal(0);
  private readonly carouselRef = viewChild<ElementRef<HTMLElement>>('carousel');

  protected readonly visiblePromos = computed(() =>
    (this.promos() ?? []).slice(0, 2)
  );

  protected readonly promoCount = computed(() => this.visiblePromos().length);

  protected readonly hasPromos = computed(() => this.promoCount() > 0);

  protected readonly bannerInlineStyle = computed(() => {
    const imageUrl = this.bannerImageUrl()?.trim();

    if (!imageUrl) return null;

    return `linear-gradient(180deg, rgba(0,0,0,0.16) 0%, rgba(0,0,0,0.58) 100%), url("${imageUrl}")`;
  });

  protected readonly resolvedBrandImage = computed(
    () =>
      this.brandImageUrl()?.trim() ||
      this.cfg.brandLogoUrl ||
      this.cfg.mascoteLogo ||
      ''
  );

  protected readonly resolvedFallbackTitle = computed(
    () => this.fallbackTitle()?.trim() || `Peça no ${this.cfg.appName}`
  );

  protected readonly resolvedFallbackSubtitle = computed(
    () =>
      this.fallbackSubtitle()?.trim() ||
      'Escolha seus favoritos e monte seu pedido com uma navegação rápida, visual e direta.'
  );

  constructor(
    @Inject(APP_CONFIG) protected readonly cfg: AppConfig,
    private readonly router: Router,
    private readonly theme: ThemeApplierService,
    @Inject(DrawerService) private readonly drawer: DrawerService,
    private readonly cart: CartService
  ) {
    this.searchInput$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((term) => this.searchSubmitted.emit(term.trim()));
  }

  protected goHome(event: Event): void {
    event.preventDefault();
    void this.router.navigateByUrl('/');
  }

  protected openCart(): void {
    this.drawer.toggle('cart');
  }

  protected toggleTheme(): void {
    this.theme.toggle();
  }

  protected goToProfile(): void {
    this.authModalOpen.set(true);
  }
  protected onAuthMascotChanged(change: AuthClientMascotChange): void {
  this.authMascotState.update((current) => ({
    ...current,
    ...change,
  }));
}

protected closeAuthModal(): void {
  this.authClient()?.reset();
  this.authClientState.set(AUTH_CLIENT_INITIAL_STATE);
  this.authMascotState.set(this.defaultAuthMascotState);
  this.authModalOpen.set(false);
}
  protected onAuthClientStateChanged(state: AuthClientState): void {
    this.authClientState.set(state);
  }

  protected onAuthModalButtonPressed(buttonId: string): void {
    if (buttonId !== 'submit-auth') {
      return;
    }

    this.authClient()?.submit();
  }

  protected onClientAuthenticated(_: ClientAuthSession): void {
    this.closeAuthModal();
  }

  protected onSearchInput(term: string): void {
    this.searchTerm = term;
    this.searchInput$.next(term);
  }

  protected onSearchSubmit(event: Event): void {
    event.preventDefault();
    // Enter dispara imediatamente, sem aguardar o debounce
    this.searchSubmitted.emit(this.searchTerm.trim());
  }

  protected selectPromo(promo: PublicHeroPromoItem): void {
    this.promoSelected.emit(promo);
  }

  protected onCarouselScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    this.activeSlide.set(index);
  }

  protected goToSlide(index: number): void {
    const el = this.carouselRef()?.nativeElement;

    if (!el) {
      return;
    }

    el.scrollTo({ left: el.clientWidth * index, behavior: 'smooth' });
  }
}
